import {
  ClientToServer,
  type GameErrorPayload,
  type GameMovePayload,
  type GameStatePayload,
  type MaskedGameState,
  type Move,
  type RoomCreatePayload,
  type RoomErrorPayload,
  type RoomGameStartingPayload,
  type RoomJoinPayload,
  type RoomKickPayload,
  type RoomStatePayload,
  ServerToClient,
} from '@completto/shared';
import type { Socket } from 'socket.io-client';
import { log } from '../lib/logger.ts';
import { pushToast, roomStore, sessionStore, socketStore } from '../stores/index.ts';

/**
 * 멀티플레이 클라이언트 어댑터.
 * Phase 4: 룸 라이프사이클 / Phase 5: 게임 동기화.
 */
export class MultiplayerService {
  constructor(private readonly socket: Socket) {
    this.bindEvents();
  }

  private bindEvents(): void {
    this.socket.on('connect', () => {
      const wasReconnecting = socketStore.get().status === 'reconnecting';
      socketStore.set({
        status: 'connected',
        socketId: this.socket.id ?? null,
        reconnectAttempt: 0,
      });
      log.info('socket.connected', { socketId: this.socket.id, wasReconnecting });
      if (wasReconnecting) {
        pushToast('서버에 다시 연결되었습니다', 'info');
        // 재연결 성공 시 마지막 룸으로 자동 재조인 (서버는 닉네임으로 재접속 처리).
        const room = roomStore.get();
        const nickname = sessionStore.get().nickname;
        if (room && nickname) {
          this.socket.emit(ClientToServer.ROOM_JOIN, {
            roomCode: room.roomCode,
            nickname,
          } satisfies RoomJoinPayload);
          log.info('socket.rejoin', { roomCode: room.roomCode });
        }
      }
    });
    this.socket.on('disconnect', (reason) => {
      // io.disconnect()로 의도된 종료가 아니면 재연결 진행.
      const willReconnect = reason !== 'io client disconnect';
      socketStore.set((s) => ({
        ...s,
        status: willReconnect ? 'reconnecting' : 'disconnected',
      }));
      log.warn('socket.disconnected', { reason, willReconnect });
    });
    this.socket.io.on('reconnect_attempt', (attempt) => {
      socketStore.set((s) => ({
        ...s,
        status: 'reconnecting',
        reconnectAttempt: attempt,
      }));
      log.info('socket.reconnect_attempt', { attempt });
    });
    this.socket.io.on('reconnect_error', (err) => {
      log.warn('socket.reconnect_error', { message: err.message });
    });
    this.socket.io.on('reconnect_failed', () => {
      socketStore.set((s) => ({ ...s, status: 'disconnected' }));
      pushToast('서버 재연결 실패. 페이지를 새로고침하세요', 'error');
      log.error('socket.reconnect_failed', {});
    });

    this.socket.on(ServerToClient.ROOM_STATE, (payload: RoomStatePayload) => {
      log.info('room.state', { code: payload.roomCode, players: payload.players.length });
      roomStore.set({ roomCode: payload.roomCode, myPlayerId: payload.myPlayerId });
      this.lastRoomState = payload;
      for (const fn of this.roomStateListeners) fn(payload);
    });

    this.socket.on(ServerToClient.ROOM_ERROR, (payload: RoomErrorPayload) => {
      log.warn('room.error', { code: payload.code, message: payload.message });
      for (const fn of this.errorListeners) fn(payload);
    });

    this.socket.on(ServerToClient.ROOM_GAME_STARTING, (payload: RoomGameStartingPayload) => {
      log.info('room.game-starting', { code: payload.roomCode });
      for (const fn of this.gameStartingListeners) fn(payload);
    });

    this.socket.on(ServerToClient.GAME_STATE, (payload: GameStatePayload) => {
      this.lastGameState = payload.state;
      log.info('game.state', {
        code: payload.state.roomCode,
        currentIdx: payload.state.currentPlayerIdx,
        status: payload.state.status,
      });
      for (const fn of this.gameStateListeners) fn(payload.state);
    });

    this.socket.on(ServerToClient.GAME_ERROR, (payload: GameErrorPayload) => {
      log.warn('game.error', { code: payload.code, message: payload.message });
      for (const fn of this.gameErrorListeners) fn(payload);
    });
  }

  private lastRoomState: RoomStatePayload | null = null;
  private lastGameState: MaskedGameState | null = null;
  private roomStateListeners = new Set<(state: RoomStatePayload) => void>();
  private errorListeners = new Set<(err: RoomErrorPayload) => void>();
  private gameStartingListeners = new Set<(p: RoomGameStartingPayload) => void>();
  private gameStateListeners = new Set<(state: MaskedGameState) => void>();
  private gameErrorListeners = new Set<(err: GameErrorPayload) => void>();

  getLastRoomState(): RoomStatePayload | null {
    return this.lastRoomState;
  }

  getLastGameState(): MaskedGameState | null {
    return this.lastGameState;
  }

  onRoomState(fn: (state: RoomStatePayload) => void): () => void {
    this.roomStateListeners.add(fn);
    if (this.lastRoomState) fn(this.lastRoomState);
    return () => {
      this.roomStateListeners.delete(fn);
    };
  }

  onError(fn: (err: RoomErrorPayload) => void): () => void {
    this.errorListeners.add(fn);
    return () => {
      this.errorListeners.delete(fn);
    };
  }

  onGameStarting(fn: (p: RoomGameStartingPayload) => void): () => void {
    this.gameStartingListeners.add(fn);
    return () => {
      this.gameStartingListeners.delete(fn);
    };
  }

  onGameState(fn: (state: MaskedGameState) => void): () => void {
    this.gameStateListeners.add(fn);
    if (this.lastGameState) fn(this.lastGameState);
    return () => {
      this.gameStateListeners.delete(fn);
    };
  }

  onGameError(fn: (err: GameErrorPayload) => void): () => void {
    this.gameErrorListeners.add(fn);
    return () => {
      this.gameErrorListeners.delete(fn);
    };
  }

  createRoom(): void {
    const nickname = sessionStore.get().nickname;
    const payload: RoomCreatePayload = { nickname };
    this.socket.emit(ClientToServer.ROOM_CREATE, payload);
  }

  joinRoom(roomCode: string): void {
    const nickname = sessionStore.get().nickname;
    const payload: RoomJoinPayload = { roomCode, nickname };
    this.socket.emit(ClientToServer.ROOM_JOIN, payload);
  }

  leaveRoom(): void {
    this.socket.emit(ClientToServer.ROOM_LEAVE, {});
    this.lastRoomState = null;
    this.lastGameState = null;
    roomStore.set(null);
  }

  kickPlayer(targetPlayerId: string): void {
    const payload: RoomKickPayload = { targetPlayerId };
    this.socket.emit(ClientToServer.ROOM_KICK, payload);
  }

  startGame(): void {
    this.socket.emit(ClientToServer.ROOM_START, {});
  }

  restartGame(): void {
    this.socket.emit(ClientToServer.ROOM_RESTART, {});
  }

  sendMove(move: Move, clientMoveId?: string): void {
    const payload: GameMovePayload = clientMoveId ? { move, clientMoveId } : { move };
    this.socket.emit(ClientToServer.GAME_MOVE, payload);
  }

  /**
   * dev cheat: 서버에 즉시 게임 종료를 요청.
   * production 서버에서는 핸들러가 없어 무시된다.
   */
  debugForceFinish(): void {
    this.socket.emit(ClientToServer.DEBUG_FORCE_FINISH, {});
  }
}
